

import React from 'react';
import { useCallback, useMemo, useRef, useContext, useState, useEffect } from 'react';

export type CustomUIRenderers<T = any, E = any> = Record<string, CustomUIRendererConfig<T, E>>;
export const CUSTOM_UI_KEY_SEPARATOR = ':::';

/**
 * Render function for a single customUI elementType.
 *
 * @param value - Current trackedObject.value of the Object this is being rendered on
 * @param externalData - Data from subscription system (undefined when no getKey), this would be other
 * data needed to render the custom UI for the given elementType beyond the trackedObject.value itself.
 * @returns JSX to render, or null for nothing
 */
export type CustomUIRenderFn<T = any, E = any> = (
  value: T,
  externalData?: E
) => React.ReactNode | null;

/**
 * Configuration for a single customUI elementType renderer.
 *
 * For simple cases where the data is not external (e.g., Computed field warnings), only `render` is needed.
 * For external data cases (e.g., MessageTemplate previews), provide `getKey`
 * to enable subscription-based data fetching.
 */
export type CustomUIRendererConfig<T = any, E = any> = {
  /**
   * Extract a lookup key from the current value. If provided, enables external
   * data mode: CustomUIDisplay will use subscribe/getData to fetch data for this key.
   * Return null if the value doesn't have enough information to form a key yet.
   *
   * Each host defines its own key strategy, so different hosts can render
   * the same @customUI elementType with different key structures.
   */
  getKey?: (value: T) => string | null;

  /** Render custom UI for this elementType. */
  render: CustomUIRenderFn<T, E>;
};

/**
 * Unified custom UI system for injecting UI into the editor.
 * Supports both sync rendering (e.g., computed field warnings) and
 * external data rendering (e.g., external data previews).
 *
 * Categories are keyed by the @customUI annotation value on the type.
 * Each elementType maps to a renderer config.
 */
export type CustomUI<T = any, E = any> = {
  /**
   * Map of elementType → renderer config.
   * Categories come from @customUI annotations on types in survey.ts.
   */
  renderers: Record<string, CustomUIRendererConfig<T, E>>;

  /**
   * Subscribe to external data updates. Only needed when renderers use getKey.
   */
  subscribe?: (elementType: string, key: string, callback: (data: E) => void) => () => void;

  /**
   * Get current external data. Only needed when renderers use getKey.
   */
  getData?: (elementType: string, key: string) => E;
};

/**
 * Hook to create a Distro CustomUI implementation with subscription management.
 *
 * Handles all the boilerplate of managing subscribers and notifying them
 * when data changes. Consumers only need to provide a renderers map and
 * call updateData when new data is available.
 *
 * IMPORTANT: The renderers map should be stable (wrapped in useMemo)
 * to prevent unnecessary re-renders of the Distro editor.
 *
 * @param renderers - Stable map of elementType → renderer config
 * @returns customUI object to pass to Distro, and updateData function to push new data
 *
 * @example
 * ```tsx
 *  const renderers = useMemo(() => ({
 *    myElementType: {
 *      getKey: (value) => {
 *        const constructedKey = // some identifying combination of values
 *        // from the object on which the custom UI is being rendered,
 *        // e.g. value.targetField + CUSTOM_UI_KEY_SEPARATOR + value.fileName
 *        return constructedKey;
 *      }
 *      // Use either the value (the trackedObject value of the type annotated by @customUI),
 *      // or the externalData (whatever is sent to updateData below) to construct the custom UI.
 *      render: (value, externalData) => {
 *        if (!value || !externalData) return null;
 *        return <MyCustomUI data={externalData} />;
 *      }
 *  }), []);
 *
 * const { customUI, updateData } = useCustomUI(renderers);
 *
 * // When data is fetched:
 * updateData('messageTemplates', compositeKey, extraData);
 *
 * // Pass to Distro:
 * <Distro customUI={customUI} ... />
 * ```
 */
export function useCustomUI<T = any, E = any>(renderers: CustomUIRenderers<T, E>): {
  customUI: CustomUI<T, E>;
  updateData: (elementType: string, key: string, data: E) => void;
} {
  // Store data by elementType/key (changes don't trigger re-renders)
  const dataRef = useRef<Record<string, Record<string, E>>>({});
  // Track subscribers for each elementType/key combination
  const subscribersRef = useRef<Map<string, Set<(data: E) => void>>>(new Map());
  // Update data for a given elementType/key combination
  // using the callback set in the subscribe function.
  const updateData = useCallback((elementType: string, key: string, data: E) => {
    // Initialize elementType if needed
    dataRef.current[elementType] ??= {};
    dataRef.current[elementType][key] = data;
    // Notify subscribers
    const subscriptionCompositeKey = `${elementType}${CUSTOM_UI_KEY_SEPARATOR}${key}`;
    const subscribers = subscribersRef.current.get(subscriptionCompositeKey);
    if (subscribers) {
      subscribers.forEach(callback => callback(data));
    }
  }, []);
  // CustomUI object identity changes only when the renderers map changes.
  // Callers should pass a stable renderers map (via useMemo) to avoid
  // unnecessary Distro re-renders.
  const customUI = useMemo<CustomUI<T, E>>(() => ({
    renderers,
    subscribe: (elementType: string, key: string, callback: (data: E) => void) => {
      const subscriptionKey = `${elementType}${CUSTOM_UI_KEY_SEPARATOR}${key}`;
      if (!subscribersRef.current.has(subscriptionKey)) {
        subscribersRef.current.set(subscriptionKey, new Set());
      }
      subscribersRef.current.get(subscriptionKey)!.add(callback);
      // Return unsubscribe function
      return () => {
        subscribersRef.current.get(subscriptionKey)?.delete(callback);
      };
    },
    getData: (elementType: string, key: string) => {
      return dataRef.current[elementType]?.[key];
    },
  }), [renderers]); // Rebuild when renderers change
  return { customUI, updateData };
}

export const CustomUIContext = React.createContext<CustomUI>({
  renderers: {},
});

/**
 * Renders custom UI for types with @customUI annotation.
 * Handles both sync (no getKey) and external data (with getKey) rendering.
 * Extracted from ObjectElement so hooks only mount for annotated types.
 * 
 * @param elementType - The @customUI annotation value on the type
 * @param value - The trackedObject.value of the Object this is being rendered on
 */
export function CustomUIDisplay<T = any, E = any>(props: {
  elementType: string;
  value: T;
}) {
  const customUI = useContext<CustomUI<T, E>>(CustomUIContext);
  const rendererConfig = customUI.renderers[props.elementType];

  // Compute key via the host's getKey function (if provided, enables external data mode)
  const compositeKey = useMemo(() => {
    return rendererConfig?.getKey?.(props.value) ?? null;
  }, [rendererConfig?.getKey, props.value]);

  // External data state (only relevant when getKey is provided)
  const [externalData, setExternalData] = useState<E | undefined>(() => {
    if (compositeKey && customUI.getData) {
      return customUI.getData(props.elementType, compositeKey);
    }
    return undefined;
  });

  // Subscribe to external data updates
  useEffect(() => {
    if (!compositeKey || !customUI.subscribe) {
      setExternalData(undefined);
      return;
    }

    const initialData = customUI.getData?.(props.elementType, compositeKey);
    setExternalData(initialData);

    // subscribe() registers setExternalData as a listener and returns a function
    // that removes it. Returning that function here tells React to call it on
    // cleanup (when the key changes or the component unmounts).
    const unsubscribe = customUI.subscribe(props.elementType, compositeKey, setExternalData);
    return unsubscribe;
  }, [customUI.getData, customUI.subscribe, props.elementType, compositeKey]);

  // No renderer registered for this elementType
  if (!rendererConfig) return null;

  // Don't render until we have external data
  if (compositeKey && !externalData) return null;

  return <>{rendererConfig.render(props.value, externalData)}</>;
}